import angular from "angular";
import { AccountingTargetListItem, AccountingTargetSettings, CompanyAccountSettings, Dates, InputEnumOption, InsuranceContract, PensionContract, SettingsLogic, SettingsStatus, Years, InvoicesLogic, Usecase, UsecaseListItem, ApiItemType, Arrays, ExportMethod, WorkerSalarySlipDeliveryMethod, Configs, Ajax, PaymentChannel } from "@salaxy/core";
import { SessionService, SettingsService, UiHelpers } from "../../services";

type SettingsSection = "pensionContracts" |
  "yelPensionContracts" |
  "insuranceContracts" |
  "incomesRegister" |
  "eInvoice" |
  "sepa" |
  "taxAndSocialSecuritySelfHandling" |
  "workerSelfHandling" |
  "unemploymentSelfHandling" |
  "payslip" |
  "omapalkka";
/**
 * Controller for the accountant settings.
 */
export class CompanySettingsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService", "SettingsService", "UiHelpers", "AjaxNg1", "$sce"];

  /** If true, current item is read only */
  public isReadOnlyForced: boolean;

  /** Possibility to restrict sections that are visible in settings. */
  public sections: SettingsSection[];

  /** Yearly sidecosts */
  public sideCosts = Years.getYearlyChangingNumbers(new Date()).sideCosts;

  /** The current editable settings */
  private _current: CompanyAccountSettings;

  private _irCertificate: any = null;

  private _openAiKey: string = null;

  private saves: { [key: string]: boolean } = {};

  constructor(private sessionService: SessionService, private settingsService: SettingsService, private uiHelpers: UiHelpers, private ajax: Ajax, private $sce: angular.ISCEService) {
  }

  /**
   * Initializes the controller
   */
  public $onInit() {
    this.sections = this.sections || ["pensionContracts", "yelPensionContracts", "insuranceContracts", "incomesRegister", "payslip", "omapalkka"];
    this._openAiKey = window.localStorage.getItem("openAiKey") || "";
  }

  /** Copy current from SettingsService */
  public get current(): CompanyAccountSettings {
    if (!this._current && this.settingsService.current) {
      this._current = angular.copy(this.settingsService.current);
    }
    return this._current;
  }


  /** Returns current pension contracts once loaded assuring there is at least one active item. */
  public get pensionContracts(): PensionContract[] | null {
    if (!this.current) {
      return null;
    }
    if (!CompanySettingsController.getActive(this.current.calc.pensionContracts)) {
      this.current.calc.pensionContracts.push({});
    }
    return this.current.calc.pensionContracts;
  }

  /** Returns current insurance contracts once loaded assuring there is at least one active item. */
  public get insuranceContracts(): InsuranceContract[] | null {
    if (!this.current) {
      return null;
    }
    if (!CompanySettingsController.getActive(this.current.calc.insuranceContracts)) {
      this.current.calc.insuranceContracts.push({});
    }
    return this.current.calc.insuranceContracts;
  }

  /**
   * Returns current default accounting target.
   */
  public get defaultAccountingTarget(): AccountingTargetSettings {
    if (!this.current?.accounting?.targets || !this.current?.accounting?.defaultTargetId) {
      return null;
    }
    return this.current.accounting.targets.find((x) => x.id == this.current?.accounting.defaultTargetId);
  }

  /**
   * Returns true, if the contract should be visible:
   * Companies: All pending contracts and first active contract.
   * Personal: The first active contract, no pending contracts.
   * @param contract Contract to evaluate
   * @param allContracts Collection of contracts that contain the contract to evaluate.
   */
  public isVisibleContract<ContractType extends PensionContract | InsuranceContract>(contract: ContractType, allContracts: ContractType[]): boolean {
    if (contract.status === SettingsStatus.Pending) {
      // Show all pending for companies. For households, this is an error / legacy.
      return this.sessionService.isInRole("company") ? true : false;
    }
    return contract === CompanySettingsController.getActive(allContracts);
  }

  /** Gets the relevant contracts that are shown in personal (household) userinterface. */
  public get personal(): {
    insurance: InsuranceContract,
    pension: PensionContract,
  } {
    const result = {
      insurance: null,
      pension: null,
    };
    if (!this.current) {
      return result;
    }
    result.insurance = CompanySettingsController.getActive(this.insuranceContracts);
    result.pension = CompanySettingsController.getActive(this.pensionContracts);
    return result;
  }

  /** Returns OPEN AI Key */
  public get openAiKey(): string {
    return this._openAiKey;
  }

  /** Sets OPEN AI Key */
  public set openAiKey(value: string) {
    this._openAiKey = value;
  }

  /** Saves open AI Key */
  public saveOpenAiKey() {
    window.localStorage.setItem("openAiKey", this._openAiKey || "");
  }

  /**
   * Saves changes for settings
   * @param section Section to save: Copies only that section to settingsService.
   * Null saves nothing.
   */
  public save(section: SettingsSection | "all" | null) {
    switch (section) {
      case "pensionContracts":
        this.settingsService.current.calc.pensionContracts = angular.copy(this.current.calc.pensionContracts);
        this.settingsService.current.payments.customerFunds.isPensionSelfHandling = this.current.payments.customerFunds.isPensionSelfHandling;
        break;
      case "insuranceContracts":
        this.settingsService.current.calc.insuranceContracts = angular.copy(this.current.calc.insuranceContracts);
        break;
      case "incomesRegister":
        this.settingsService.current.calc.incomesRegister = angular.copy(this.current.calc.incomesRegister);
        break;
      case "eInvoice":
        this.settingsService.current.payments.invoice.eInvoiceReceiver = this.current.payments.invoice.eInvoiceReceiver;
        this.settingsService.current.payments.invoice.eInvoiceIntermediator = this.current.payments.invoice.eInvoiceIntermediator;
        break;
      case "sepa":
        this.settingsService.current.payments.invoice.sepaBankPartyId = this.current.payments.invoice.sepaBankPartyId;
        this.settingsService.current.payments.invoice.ibanNumber = this.current.payments.invoice.ibanNumber;
        break;
      case "taxAndSocialSecuritySelfHandling":
        this.settingsService.current.payments.customerFunds.isTaxAndSocialSecuritySelfHandling = this.current.payments.customerFunds.isTaxAndSocialSecuritySelfHandling;
        break;
      case "workerSelfHandling":
        this.settingsService.current.payments.customerFunds.isWorkerSelfHandling = this.current.payments.customerFunds.isWorkerSelfHandling;
        break;
      case "unemploymentSelfHandling":
        return;
      case "payslip": {
        const methods = this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.filter(x => x !== WorkerSalarySlipDeliveryMethod.Payslip);
        this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.splice(0, this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.length);
        this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.push(...methods);
        if (this.current.messaging.workerSalarySlipDeliveryMethods.indexOf(WorkerSalarySlipDeliveryMethod.Payslip) > -1) {
          this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.push(WorkerSalarySlipDeliveryMethod.Payslip);
        }
        break;
      }
      case "omapalkka": {
        const methods = this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.filter(x => x !== WorkerSalarySlipDeliveryMethod.Omapalkka);
        this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.splice(0, this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.length);
        this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.push(...methods);
        if (this.current.messaging.workerSalarySlipDeliveryMethods.indexOf(WorkerSalarySlipDeliveryMethod.Omapalkka) > -1) {
          this.settingsService.current.messaging.workerSalarySlipDeliveryMethods.push(WorkerSalarySlipDeliveryMethod.Omapalkka);
        }
        break;
      }
      case "all":
        this.settingsService.current = angular.copy(this.current);
        break;
    }
    if (section) {
      this.saves[section] = true;
      this.settingsService.save().then((data) => {
        switch (section) {
          case "pensionContracts":
            this.current.calc.pensionContracts = angular.copy(data.calc.pensionContracts);
            this.current.payments.customerFunds.isPensionSelfHandling = data.payments.customerFunds.isPensionSelfHandling;
            break;
          case "insuranceContracts":
            this.current.calc.insuranceContracts = angular.copy(data.calc.insuranceContracts);
            break;
          case "incomesRegister":
            this.current.calc.incomesRegister = angular.copy(data.calc.incomesRegister);
            break;
          case "eInvoice":
            this.current.payments.invoice.eInvoiceReceiver = data.payments.invoice.eInvoiceReceiver;
            this.current.payments.invoice.eInvoiceIntermediator = data.payments.invoice.eInvoiceIntermediator;
            break;
          case "sepa":
            this.current.payments.invoice.sepaBankPartyId = data.payments.invoice.sepaBankPartyId;
            this.current.payments.invoice.ibanNumber = data.payments.invoice.ibanNumber;
            break;
          case "taxAndSocialSecuritySelfHandling":
            this.current.payments.customerFunds.isTaxAndSocialSecuritySelfHandling = data.payments.customerFunds.isTaxAndSocialSecuritySelfHandling;
            break;
          case "workerSelfHandling":
            this.current.payments.customerFunds.isWorkerSelfHandling = data.payments.customerFunds.isWorkerSelfHandling;
            break;
          case "payslip":
          case "omapalkka":
            this.current.messaging.workerSalarySlipDeliveryMethods = data.messaging.workerSalarySlipDeliveryMethods;
            break;
          case "all":
            this._current = angular.copy(data);
            break;
        }
        this.saves[section] = false;
      });
    }
  }

  /**
   * Creates a new IR certificate.
   * @param transferId Transfer ID for the IR certificate.
   * @param transferPassword One time transfer password for the IR certificate.
   * @returns Information about the created IR certificate.
   */
  public async createIrCertificate(transferId: string, transferPassword: string): Promise<void> {
    this.saves["irCertificate"] = true;
    try {
      await this.settingsService.createIrCertificate(transferId, transferPassword);
    } finally {
      this.saves["irCertificate"] = false;
      this._irCertificate = null;
    }
    return Promise.resolve();
  }

  /**
   * Renews an existing IR certificate.
   * @returns Information about the created IR certificate.
   */
  public async renewIrCertificate(): Promise<void> {
    this.saves["irCertificate"] = true;
    try {
      await this.settingsService.renewIrCertificate();
    } finally {
      console.log("renewIrCertificate");
      this.saves["irCertificate"] = false;
      this._irCertificate = null;
    }
    return Promise.resolve();
  }

  /** Returns true if the account has chosen to use own certificate */
  public hasOwnIrCertificate(): boolean {
    return this.settingsService.current?.calc?.incomesRegister?.irDelivery?.senderOfficialId != null &&
    this.settingsService.current?.calc?.incomesRegister?.irDelivery?.senderOfficialId == this.sessionService.session?.currentAccount?.identity?.officialId;
  }

  /** Current IR certificate */
  public get irCertificate(): any {
    if (this.hasOwnIrCertificate()) {
      if (!this._irCertificate) {
        this._irCertificate = {
          id : null
        };
        this.settingsService.getIrCertificate().then((result) => {
          if (result?.id) {
            angular.copy(result, this._irCertificate);
          }
        });
      }
    }
    return this._irCertificate;
  }

  /**
   * Send all workers paid calculations to omapalkka
   */
  public sendPaidCalculationsToOmapalkka() {
    const loading = this.uiHelpers.showLoading();
    this.ajax.postJSON(`${Configs.current.getOmapalkkaApiServer()}/api/messages/create-for-salary/all`, {}).then(() => {
      loading.dismiss();
    }).catch(e => {
      console.error(e);
      loading.dismiss();
    });
  }

  /** Checks if switch input should be disabled on salarySlipDeliveryMethodsForm*/
  public disableSwitchInput(): boolean {
    return this.isSaving("omapalkka") || this.isSaving("payslip") || !!this.isReadOnlyForced || (!this.current.messaging.denyServiceModel && this.current.partner.serviceModel.features.messaging);
  }

  /**
   * Sets pension number for test account to the given contract
   * @param item Pension contract
   */
  public setPensionNumberForTest(item: PensionContract) {
    item.contractNumber = SettingsLogic.getPensionNumberForTest(item.company);
  }

  /**
   * Shows the eInvoice selection dialog
   */
  public selectEInvoiceAddress() {
    this.settingsService.selectEInvoiceAddress(this.current.payments.invoice);
  }

  /**
   * Gets the progress of save operation.
   * @param section Section to check for saving.
   * Note that "all" will check only "all" operations, not underlying sub-sections saving ("any").
   */
  public isSaving(section: SettingsSection | "all"): boolean {
    return !!(this.saves[section] || this.saves["all"]);
  }

  /**
   * Event handler for accounting target selection.
   * @param current Settings object to edit/create.
   * @param logic Logic object passed to the dialog controller.
   * @param logic.isReadOnly Read-only flag in logic.
   * @param value Boolean indicating if the item is selected or not.
   * @param item  The selected item.
   * @param allItems All selected items.
   */
  public onAccountingTargetSelect(current: AccountingTargetSettings, logic: { isReadOnly: boolean }, value: boolean, item: AccountingTargetListItem, allItems: AccountingTargetListItem[]) {
    allItems.splice(0, allItems.length);
    logic.isReadOnly = true;
    if (value == true) {
      current.id = item.id;
      current.avatar = item.otherPartyInfo.avatar;
      current.format = item.data.format;
      current.channel = item.data.channel;
      current.method = item.data.method;
      current.availableMethods = Arrays.assureArray(item.data.availableMethods) as ExportMethod[];
      logic.isReadOnly = false;
      allItems.push(item);
    }
  }

  /**
   * Event handler for usecase selection.
   * @param current Settings object to edit/create.
   * @param logic Logic object passed to the dialog controller.
   * @param logic.isReadOnly Read-only flag in logic.
   * @param value Boolean indicating if the item is selected or not.
   * @param item  The selected item.
   * @param allItems All selected items.
   */
  public onUsecaseSelect(current: Usecase, logic: { isReadOnly: boolean }, value: boolean, item: UsecaseListItem, allItems: UsecaseListItem[]) {
    allItems.splice(0, allItems.length);
    logic.isReadOnly = true;
    if (value == true) {
      current.uri = item.reference;
      current.label = item.shortText;
      current.itemType = item.entityType as ApiItemType;
      current.status = item.status;
      current.order = item.data.order;
      logic.isReadOnly = false;
      allItems.push(item);
    }
  }

  /**
   * Return Avatar from current.payment.channels ENUM
   * @returns Avatar
   */
  public getPaymentChannelAvatar(item) {
    return InvoicesLogic.getChannelAvatar(item.channel);
  }

  /**
   * Created InputEnumOption [] from current.accounting.targets
   * @returns InputEnumOption [] for template
   */
  public getSelectableAccountingTargets(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "selectableAccountingTargets", () => {
      return this.current.accounting.targets.map(item => ({
        text: item.avatar.displayName,
        value: item.id,
        ui: item.avatar
      }) as InputEnumOption);
    }, () => this.current.accounting.targets.map(item => item.id)
    );
  }

  /**
   * Created InputEnumOption [] from current.payment.channels
   * @returns InputEnumOption [] for template
   */

  public getSelectablePaymenMethods(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "selectablePaymentMethods", () => {
      return this.current.payments.channels.map(item => {
        const avatar = InvoicesLogic.getChannelAvatar(item.channel) ?? InvoicesLogic.getChannelAvatar(PaymentChannel.Undefined);
        return {
          text: avatar.displayName,
          value: avatar.id,
          ui: avatar
        } as InputEnumOption;
      });
    }, () => this.current.payments.channels.map(item => InvoicesLogic.getChannelAvatar(item.channel)?.id)
    );
  }

  /**
   * Returns true if the payslip is included in worker's SalarySlipDeliveryMethods.
   */
  public get payslip(): boolean {
    return this.current?.messaging?.workerSalarySlipDeliveryMethods?.indexOf(WorkerSalarySlipDeliveryMethod.Payslip) >= 0 || false;
  }

  /**
   * Sets/unsets the payslip as one of worker's SalarySlipDeliveryMethods.
   * @param value Boolean value if the payslip should be set.
   */
  public set payslip(value: boolean) {
    if (!this.current) {
      return;
    }
    if (!value) {
      const methods = this.current.messaging.workerSalarySlipDeliveryMethods.filter(x => x !== WorkerSalarySlipDeliveryMethod.Payslip);
      this.current.messaging.workerSalarySlipDeliveryMethods.splice(0, this.current.messaging.workerSalarySlipDeliveryMethods.length);
      this.current.messaging.workerSalarySlipDeliveryMethods.push(...methods);
    } else {
      if (this.current.messaging.workerSalarySlipDeliveryMethods.indexOf(WorkerSalarySlipDeliveryMethod.Payslip) < 0) {
        this.current.messaging.workerSalarySlipDeliveryMethods.push(WorkerSalarySlipDeliveryMethod.Payslip);
      }
    }
    this.save("payslip");
  }

  /**
   * Returns true if the omapalkka is included in worker's SalarySlipDeliveryMethods.
   */
  public get omapalkka(): boolean {
    return this.current?.messaging?.workerSalarySlipDeliveryMethods?.indexOf(WorkerSalarySlipDeliveryMethod.Omapalkka) >= 0 || false;
  }

  /**
   * Sets/unsets the omapalkka as one of worker's SalarySlipDeliveryMethods.
   * @param value Boolean value if the payslip should be set.
   */
  public set omapalkka(value: boolean) {
    if (!this.current) {
      return;
    }
    if (!value) {
      const methods = this.current.messaging.workerSalarySlipDeliveryMethods.filter(x => x !== WorkerSalarySlipDeliveryMethod.Omapalkka);
      this.current.messaging.workerSalarySlipDeliveryMethods.splice(0, this.current.messaging.workerSalarySlipDeliveryMethods.length);
      this.current.messaging.workerSalarySlipDeliveryMethods.push(...methods);
    } else {
      if (this.current.messaging.workerSalarySlipDeliveryMethods.indexOf(WorkerSalarySlipDeliveryMethod.Omapalkka) < 0) {
        this.current.messaging.workerSalarySlipDeliveryMethods.push(WorkerSalarySlipDeliveryMethod.Omapalkka);
      }
    }
    this.save("omapalkka");
  }

  /**
   * Confirm service change
   */
  public confirmServiceModelChange() {
    this.uiHelpers.showConfirm("Palvelumallin ohitus", "Olet muuttamassa palvelumallin käyttöä. Paina 'Ok' tallentaaksesi").then((result) => {
      if (result) {
        this.save("all");
      }
    });
  }

  /**
   * Creates datauri for given image
   * @param logo Logo for dataurl
   */
  public createReportLogoDatauri(logo: any) {
    const createDataUri = (logo): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.onerror = (error) => {
          reject(error);
        };
        reader.readAsDataURL(logo);
      });
    };
    createDataUri(logo).then(dataUri => {
      this.current.reporting.logo = `<img src="${dataUri}">`;
    });
  }

  /**
   * Retrieves the report logo.
   * @returns The report logo as a html string.
   */
  public getReportLogo(): string {
    if (!this.current.reporting.logo) {
      return "";
    }

    if (this.current.reporting.logo.startsWith("<img src=")) {
      return this.current.reporting.logo;
    }

    if (this.current.reporting.logo.startsWith("<svg")) {
      return this.$sce.trustAsHtml(this.current.reporting.logo);
    }
  }

  private static getActive<ContractType extends PensionContract | InsuranceContract>(contracts: ContractType[]): ContractType {
    const today = Dates.getToday();
    return contracts?.find((x) => x.status !== SettingsStatus.Pending &&
      Dates.isSameOrAfter(today, x.startDate ?? "1900-01-01") &&
      Dates.isBefore(today, Dates.add(x.endDate ?? "2100-01-01", 1, "day")));
  }
}

